import { Link } from "react-router-dom";
import {
  Dog,
  Scissors,
  Home as HomeIcon,
  Soup,
  Fish,
  ArrowRight,
  Sparkles,
  HeartHandshake,
  Leaf,
  Shield,
  Award,
  Users,
  Star,
} from "lucide-react";
import { SITE, IMG } from "@/lib/site";
import SectionHeading from "@/components/SectionHeading";
import CtaSection from "@/components/CtaSection";

const services = [
  {
    icon: Dog,
    title: "Allevamento",
    desc: "Pastori tedeschi selezionati con cura: salute, morfologia e carattere.",
    to: "/allevamento",
    testId: "service-card-allevamento",
  },
  {
    icon: Scissors,
    title: "Toelettatura",
    desc: "Igiene, taglio e cura del mantello in un ambiente sereno e professionale.",
    to: "/toelettatura",
    testId: "service-card-toelettatura",
  },
  {
    icon: HomeIcon,
    title: "Pensione per cani",
    desc: "Una seconda casa per il tuo amico, aperta tutto l'anno.",
    to: "/pensione",
    testId: "service-card-pensione",
  },
  {
    icon: Soup,
    title: "Cibo per cani",
    desc: "Alimenti selezionati e consulenza personalizzata per ogni esigenza.",
    to: "/cibo",
    testId: "service-card-cibo",
  },
  {
    icon: Fish,
    title: "Negozio animali & Pesca",
    desc: "Accessori per animali e articoli da pesca: tutto in un unico luogo.",
    to: "/negozio",
    testId: "service-card-negozio",
  },
];

const reasons = [
  { icon: Award, title: "Oltre 20 anni di esperienza", desc: "Allevamento riconosciuto ENCI dal 2000." },
  { icon: HeartHandshake, title: "Benessere prima di tutto", desc: "Ogni animale è seguito con cura quotidiana." },
  { icon: Leaf, title: "Ambiente naturale e curato", desc: "Spazi ampi, ordinati e sicuri tra i colli mantovani." },
  { icon: Shield, title: "Servizi completi", desc: "Allevamento, toelettatura, pensione e negozio in un unico luogo." },
  { icon: Users, title: "Consulenza personalizzata", desc: "Ti accompagniamo nella scelta più adatta al tuo cane." },
  { icon: Sparkles, title: "Rapporto familiare", desc: "Nicola ed Elisa al tuo fianco, con la passione di sempre." },
];

const testimonials = [
  {
    name: "Famiglia Bianchi",
    role: "Proprietari di Luna",
    text: "Abbiamo preso il nostro cucciolo da Nicola ed Elisa: cura, attenzione e supporto anche dopo la consegna. Un'esperienza che ci ha conquistati.",
  },
  {
    name: "Marco T.",
    role: "Cliente pensione",
    text: "Lascio il mio pastore tedesco in pensione ogni estate. Torna sempre felice e ben curato. Posso partire serenamente.",
  },
  {
    name: "Giulia R.",
    role: "Clienti toelettatura",
    text: "Toelettatura gentile e precisa, anche con cani timidi. Si vede l'amore vero per gli animali in ogni dettaglio.",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative pt-12 pb-16 sm:pt-20 sm:pb-24" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-semibold text-[#7A1F1F] mb-5">
              Allevamento riconosciuto ENCI · Dal 2000
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1A1710]">
              Allevamento <em className="italic text-[#7A1F1F]">Colli Storici</em>
            </h1>
            <p className="mt-5 font-sans font-medium text-xl sm:text-2xl text-[#6B5F4A] leading-snug italic">
              Cura, passione e servizi completi <br className="hidden sm:block" />
              per il mondo degli animali.
            </p>
            <p className="mt-6 text-base sm:text-lg text-[#6B5F4A] leading-relaxed max-w-xl">
              Da oltre vent'anni ci prendiamo cura degli animali con professionalità,
              attenzione e amore, offrendo servizi di allevamento, toelettatura,
              pensione, alimentazione, negozio per animali e articoli per la pesca.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#servizi"
                data-testid="hero-services-btn"
                className="inline-flex bg-[#7A1F1F] text-white hover:bg-[#5C1717] rounded-full px-7 py-3.5 text-sm font-semibold transition-colors"
              >
                Scopri i nostri servizi
              </a>
              <Link
                to="/contatti"
                data-testid="hero-contact-btn"
                className="inline-flex border border-[#1A1710]/20 text-[#1A1710] hover:border-[#7A1F1F] hover:text-[#7A1F1F] rounded-full px-7 py-3.5 text-sm font-semibold transition-colors"
              >
                Contattaci
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8">
              <div>
                <div className="font-serif text-4xl text-[#1A1710]">{SITE.yearsExperience}</div>
                <div className="text-xs uppercase tracking-widest text-[#7A1F1F] font-semibold">anni di esperienza</div>
              </div>
              <div className="w-px h-12 bg-[#D9C48A]" />
              <div>
                <div className="font-serif text-4xl text-[#1A1710]">ENCI</div>
                <div className="text-xs uppercase tracking-widest text-[#7A1F1F] font-semibold">riconosciuto</div>
              </div>
              <div className="w-px h-12 bg-[#D9C48A]" />
              <div>
                <div className="font-serif text-4xl text-[#1A1710]">6</div>
                <div className="text-xs uppercase tracking-widest text-[#7A1F1F] font-semibold">servizi dedicati</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 animate-fade-up delay-200">
            <div className="relative">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-md">
                <img src={IMG.hero} alt="Pastore tedesco dell'allevamento Colli Storici" className="w-full h-full object-cover" />
              </div>
              <div className="hidden sm:block absolute -bottom-6 -left-6 w-44 h-56 rounded-3xl overflow-hidden ring-4 ring-[#F5E6C0] shadow-md">
                <img src={IMG.cuccioli} alt="Cuccioli pastore tedesco" className="w-full h-full object-cover" />
              </div>
              <div className="hidden md:block absolute -top-6 -right-6 bg-[#F5E6C0] rounded-2xl px-5 py-4 shadow-md border border-[#D9C48A]">
                <div className="flex items-center gap-2 text-[#7A1F1F]">
                  <Star className="w-4 h-4 fill-[#8B4A2B] text-[#8B4A2B]" />
                  <span className="font-semibold text-[#1A1710] text-sm">Passione dal 2000</span>
                </div>
                <p className="text-xs text-[#6B5F4A] mt-1">Selezione attenta e dedicata</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section id="servizi" className="py-20 sm:py-28 bg-[#EDD895]/60" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <SectionHeading
            overline="I nostri servizi"
            title="Tutto ciò di cui il tuo animale ha bisogno, in un solo luogo."
            lead="Cinque servizi pensati per accompagnare la vita del tuo cane: dalla scelta del cucciolo al suo benessere quotidiano."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={s.testId}
                className="reveal group bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-8 hover:shadow-md transition-all hover:-translate-y-1"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#EDD895] grid place-items-center mb-6 group-hover:bg-[#7A1F1F] group-hover:text-white transition-colors text-[#7A1F1F]">
                  <s.icon className="w-7 h-7" strokeWidth={1.4} />
                </div>
                <h3 className="font-serif text-2xl text-[#1A1710]">{s.title}</h3>
                <p className="mt-3 text-[#6B5F4A] leading-relaxed">{s.desc}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#7A1F1F] group-hover:gap-3 transition-all">
                  Scopri di più <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PERCHÉ NOI */}
      <section className="py-20 sm:py-28" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <SectionHeading
              overline="Perché scegliere Colli Storici"
              title="Esperienza, cura e rispetto in ogni gesto quotidiano."
              lead="Da oltre vent'anni dedichiamo ogni giornata al benessere dei nostri cani e di quelli che ci vengono affidati."
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {reasons.map((r, i) => (
              <div
                key={r.title}
                className="reveal bg-[#F5E6C0] border border-[#D9C48A] rounded-2xl p-6"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-11 h-11 rounded-xl bg-[#EDD895] grid place-items-center mb-4 text-[#8B4A2B]">
                  <r.icon className="w-5 h-5" strokeWidth={1.4} />
                </div>
                <h4 className="font-sans font-semibold text-[#1A1710] text-lg">{r.title}</h4>
                <p className="mt-2 text-sm text-[#6B5F4A] leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORIA */}
      <section className="py-20 sm:py-28 bg-[#EDD895]/60" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1 reveal">
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden">
                <img src={IMG.cristal} alt="Cristal dei Colli Storici" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] rounded-3xl overflow-hidden mt-10">
                <img src={IMG.hero2} alt="Allevamento Colli Storici" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 reveal">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-semibold text-[#7A1F1F] mb-4">
              La nostra storia
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1A1710]">
              Una passione che si è fatta lavoro, ogni giorno.
            </h2>
            <p className="mt-6 text-[#6B5F4A] leading-relaxed text-lg">
              L'obiettivo del nostro allevamento di pastori tedeschi è la
              ricerca scrupolosa e selettiva dei migliori riproduttori e dei
              migliori accoppiamenti, per ottenere cuccioli il più possibile
              corrispondenti allo standard di razza, con particolare attenzione
              a <strong>salute, morfologia e carattere</strong>.
            </p>
            <p className="mt-4 text-[#6B5F4A] leading-relaxed">
              All'allevamento attivo e riconosciuto dall'ENCI da oltre vent'anni
              si affianca, dal 2024, anche un servizio di <strong>pensione per cani</strong>{" "}
              aperta tutto l'anno, dove gli ospiti vengono trattati e accuditi
              con la stessa cura e la stessa attenzione rivolta abitualmente ai
              nostri cani.
            </p>
            <p className="mt-4 text-[#6B5F4A] leading-relaxed">
              Titolare della struttura è <strong>{SITE.owner}</strong> che,
              coadiuvato dalla moglie {SITE.partner}, è riuscito a fare della
              passione per i cani un lavoro a tempo pieno.
            </p>
            <Link
              to="/chi-siamo"
              data-testid="story-link"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#7A1F1F] hover:gap-3 transition-all"
            >
              Conosci la nostra storia <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIANZE */}
      <section className="py-20 sm:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <SectionHeading
            align="center"
            overline="Dicono di noi"
            title="Le voci di chi ci ha scelto."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                className="reveal bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-8"
                data-testid={`testimonial-${i}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex gap-1 text-[#8B4A2B]">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="mt-5 font-serif text-xl leading-relaxed text-[#1A1710]">
                  "{t.text}"
                </p>
                <div className="mt-6 pt-6 border-t border-[#D9C48A]">
                  <div className="font-semibold text-[#1A1710]">{t.name}</div>
                  <div className="text-sm text-[#7A1F1F]">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
